import React from 'react';
import { Table, TableColumn } from 'common/components/Table';
import { Tooltip } from 'common/components';
import DropdownMenu from '../DropdownMenu';
import { Workflow } from 'pages/AgenticWorkflow/AgenticWorkflow.types';
import { Bot, PhoneForwarded, Info, Moon, Clock } from 'lucide-react';
import { isOutsideOperatingHours, formatOperatingHoursTooltip } from 'utils/operatingHours';

import styles from './WorkflowsTable.module.scss';

interface WorkflowsTableProps {
    workflows: Workflow[];
    loading: boolean;
    onRowClick: (workflow: Workflow) => void;
    actionsDisabled?: boolean;
    onDeleteSuccess: () => void;
    onUploadClick: (workflow: Workflow) => void;
}

const WorkflowsTable: React.FC<WorkflowsTableProps> = ({
    workflows,
    loading,
    onRowClick,
    actionsDisabled = false,
    onDeleteSuccess,
    onUploadClick,
}) => {
    // Format date to display in a readable format
    const formatDate = (dateString?: string): string => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString();
    };

    const columns: TableColumn<Workflow>[] = [
        {
            key: 'title',
            title: 'Name',
            render: (_, record) => {
                const oh = record.config?.operatingHours;
                const hasOperatingHours = !!oh;
                const outside = isOutsideOperatingHours(record.config);
                const is24h = oh?.startTime === '00:00' && oh?.endTime === '23:59';
                return (
                    <span className='inline-flex items-center gap-xs'>
                        {record.title}
                        {hasOperatingHours &&
                            (outside ? (
                                <Tooltip content={formatOperatingHoursTooltip(oh)} maxWidth={350}>
                                    <Moon
                                        size={14}
                                        color='var(--gray-700)'
                                        aria-label='Outside operating hours'
                                        tabIndex={0}
                                    />
                                </Tooltip>
                            ) : is24h ? (
                                <Tooltip content='24 Hour operating hours' maxWidth={350}>
                                    <Clock
                                        size={14}
                                        color='var(--success-color)'
                                        aria-label='24 hour operating hours'
                                        tabIndex={0}
                                    />
                                </Tooltip>
                            ) : null)}
                    </span>
                );
            },
        },
        {
            key: 'description',
            title: 'Description',
            dataIndex: 'description',
        },
        {
            key: 'human_transfer',
            title: 'Human Handoff',
            render: (_, record) =>
                record.human_transfer ? (
                    <div className='flex items-center gap-sm'>
                        <PhoneForwarded size={16} color='var(--primary-purple-dark)' />
                        Enabled
                    </div>
                ) : (
                    <div className='flex items-center gap-sm'>
                        <Bot size={18} color='var(--success-color)' />
                        Disabled
                    </div>
                ),
        },
        {
            key: 'status',
            title: 'Status',
            render: (_, record) => (
                <span className={`${styles.status} ${styles[record.status?.toLowerCase() as keyof typeof styles]}`}>
                    {record.status || 'Active'}
                </span>
            ),
        },
        {
            key: 'total_calls',
            title: 'Total Calls',
            render: (_, record) => record.total_calls || 0,
        },
        {
            key: 'pending_calls',
            title: (
                <span className='inline-flex items-center gap-xs'>
                    Pending Calls
                    <Tooltip
                        content="Calls waiting to enter the queue. These haven't reached their scheduled date or are outside business hours."
                        maxWidth={300}
                    >
                        <Info size={14} className={styles.infoIcon} tabIndex={0} aria-label='Pending Calls info' />
                    </Tooltip>
                </span>
            ),
            render: (_, record) => record.pending_calls || 0,
        },
        {
            key: 'total_contacts',
            title: 'Total Contacts',
            render: (_, record) => record.total_contacts || 0,
        },
        {
            key: 'start_date',
            title: 'Start Date',
            render: (_, record) => formatDate(record.start_date),
        },
        {
            key: 'end_date',
            title: 'End Date',
            render: (_, record) => formatDate(record.end_date),
        },
        {
            key: 'created_at',
            title: 'Creation Date',
            render: (_, record) => formatDate(record.created_at || new Date().toISOString()),
        },
        {
            key: 'actions',
            title: 'Actions',
            render: (_, record) => (
                <div onClick={(e) => e.stopPropagation()}>
                    <DropdownMenu
                        workflow={record}
                        disabled={actionsDisabled}
                        onDeleteSuccess={onDeleteSuccess}
                        onUploadClick={onUploadClick}
                    />
                </div>
            ),
        },
    ];

    return (
        <div className={styles.tableContainer}>
            <Table
                data={workflows}
                columns={columns}
                isLoading={loading}
                rowKey='id'
                onRowClick={(workflow) => onRowClick(workflow)}
                hoverable
                variant='primary'
                emptyText='No workflows found'
            />
        </div>
    );
};

export default WorkflowsTable;
